import { useEffect, useRef, useState } from 'react';
import { hashKey, keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { ApiError, getApiClient } from '@signalvote/api-client';
import type {
  InterpretSegmentRequest,
  Segment,
  SegmentArea,
  VoteCountsAnswer,
  VoteCountsResponse,
  VoterCounts,
  VoterCountsAnswer,
  VoterDataGeography,
} from '@signalvote/shared';
import { VOTER_DATA_PLACE_SEARCH_MAX_CHARS } from '@signalvote/shared';
import { queryKeys } from '@/lib/queryKeys';
import type { SegmentKey, VoterPlaceSearch } from '@/lib/voterSegmentKey';

const api = getApiClient();

/** L2's lockout after its rate limit trips was measured at about 2.5 minutes; retrying sooner only fails fast again. */
const RATE_LIMIT_FALLBACK_DELAY_MS = 180_000;
const MAX_RATE_LIMIT_RETRIES = 3;

/**
 * A count the server couldn't make yet because L2's shared budget is spent. Thrown so React Query
 * keeps the last numbers on screen and asks again; it is never shown as an error.
 */
export class VoterDataPendingError extends Error {
  constructor(readonly retryAfterSeconds: number) {
    super('Voter counts are pending.');
  }
}

/** A held count is only what L2 counted: its price was for the one per household it was asked with, which the builder works out itself. */
async function countsOf(answer: Promise<VoterCountsAnswer>): Promise<VoterCounts> {
  const settled = await answer;
  if (settled.status === 'Pending') throw new VoterDataPendingError(settled.retryAfterSeconds);
  const { status: _status, records: _records, priceCents: _priceCents, belowMinimum: _belowMinimum, ...counts } = settled;
  return counts;
}

async function voteCountsOf(answer: Promise<VoteCountsAnswer>): Promise<VoteCountsResponse> {
  const settled = await answer;
  if (settled.status === 'Pending') throw new VoterDataPendingError(settled.retryAfterSeconds);
  const { status: _status, ...votes } = settled;
  return votes;
}

/**
 * A pending count is asked again for as long as it's on screen. Otherwise only an L2 rate limit
 * (429) is retried, a few times. Anything else fails at once: an immediate retry would spend more
 * of L2's budget.
 */
export function voterDataRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof VoterDataPendingError) return true;
  return error instanceof ApiError && error.status === 429 && failureCount < MAX_RATE_LIMIT_RETRIES;
}

/** As long as the server said to wait. */
export function voterDataRetryDelay(_attempt: number, error: unknown): number {
  if (error instanceof VoterDataPendingError) return error.retryAfterSeconds * 1000;
  return error instanceof ApiError && error.retryAfterSeconds !== null
    ? error.retryAfterSeconds * 1000
    : RATE_LIMIT_FALLBACK_DELAY_MS;
}

/**
 * The Voter data builder's catalog: the states, the subsidiary app's fields, and once `state` is
 * given, its districts. It barely changes, so it is kept for the session.
 */
export function useVoterDataCatalog(state?: string, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    enabled,
    queryKey: queryKeys.voterData.catalog(state),
    queryFn: () => api.voterData.getCatalog({ state }),
    staleTime: Infinity,
    retry: voterDataRetry,
    retryDelay: voterDataRetryDelay,
  });
}

function searchPlaces(search: VoterPlaceSearch) {
  return api.voterData.searchPlaces({ state: search.state, kind: search.kind ?? undefined, q: search.q, county: search.county ?? undefined });
}

/**
 * The district a campaign's race is elected from, as the server's matcher picks it, with whether it is sure
 * enough to suggest. Asked once per race and state for the session; no match is an answer, never an error.
 */
export function useRaceMatch(state: string, race: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.voterData.raceMatch(state, race),
    queryFn: () => api.voterData.matchRace({ state, race }),
    enabled: enabled && state !== '' && race.trim() !== '',
    staleTime: Infinity,
    retry: voterDataRetry,
    retryDelay: voterDataRetryDelay,
  });
}

/** The places of one kind a search finds, best first, with how many matched in all. Null searches nothing. */
export function useVoterPlaceSearch(search: VoterPlaceSearch | null) {
  return useQuery({
    queryKey: queryKeys.voterData.places(search),
    queryFn: () => searchPlaces(search!),
    enabled: search !== null,
    // A cleared search shows nothing, not the last one's results.
    placeholderData: search ? keepPreviousData : undefined,
    // Searches read the state's saved place list, which changes about monthly.
    staleTime: Infinity,
    retry: voterDataRetry,
    retryDelay: voterDataRetryDelay,
  });
}

/**
 * Names for places known only by their codes, such as a quoted order's, by kind: a search finds a
 * place by its code too, so each is looked up by searching for it. Codes not found yet are left out.
 */
export function useVoterPlaceLabels(state: string, areas: SegmentArea[]): Partial<Record<VoterDataGeography, Record<string, string>>> {
  const searches = areas.flatMap(({ geography, codes }) =>
    codes.map((code) => ({ kind: geography, code, search: { state, kind: geography, q: code.slice(0, VOTER_DATA_PLACE_SEARCH_MAX_CHARS), county: null } })),
  );
  const results = useQueries({
    queries: searches.map(({ search }) => ({
      queryKey: queryKeys.voterData.places(search),
      queryFn: () => searchPlaces(search),
      enabled: state !== '',
      staleTime: Infinity,
      retry: voterDataRetry,
      retryDelay: voterDataRetryDelay,
    })),
  });
  const labels: Partial<Record<VoterDataGeography, Record<string, string>>> = {};
  results.forEach((result, i) => {
    const { kind, code } = searches[i];
    const place = result.data?.places.find((p) => p.code === code);
    if (place) labels[kind] = { ...labels[kind], [code]: place.label };
  });
  return labels;
}

/**
 * How long the segment must hold still before it is counted. L2's rate limit is shared by every
 * customer, so a burst of clicks is counted once, at its end.
 */
export const COUNTS_SETTLE_MS = 1000;

/** L2 refreshes a state's file about twice a year, so an answer stays right for the whole session. */
const heldForTheSession = { staleTime: Infinity, gcTime: Infinity } as const;

function useSettled(segment: Segment | null, atOnce: boolean): { settled: Segment | null; settling: boolean } {
  const [settled, setSettled] = useState(segment);
  const latest = useRef(segment);
  useEffect(() => {
    latest.current = segment;
  }, [segment]);
  const wanted = hashKey(queryKeys.voterData.counts(segment));
  const current = hashKey(queryKeys.voterData.counts(settled));
  if (atOnce && wanted !== current) setSettled(segment);
  useEffect(() => {
    if (wanted === current) return;
    const timer = setTimeout(() => setSettled(latest.current), COUNTS_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [wanted, current]);
  return { settled, settling: wanted !== current };
}

export function useVoterCounts(segment: Segment | null) {
  const client = useQueryClient();
  const held = segment !== null && client.getQueryState(queryKeys.voterData.counts(segment))?.data !== undefined;
  const { settled, settling } = useSettled(segment, held);
  const query = useQuery({
    queryKey: queryKeys.voterData.counts(settled),
    queryFn: ({ signal }) => countsOf(api.voterData.getCounts(settled!, { signal })),
    enabled: settled !== null,
    placeholderData: keepPreviousData,
    ...heldForTheSession,
    retry: voterDataRetry,
    retryDelay: voterDataRetryDelay,
  });
  return segment !== null && settling ? ({ ...query, isFetching: true, isPlaceholderData: true } as typeof query) : query;
}

/**
 * What a field's detail is measured against: `segment` without `fieldId`'s rule. Asking waits for
 * it to settle, like a count; reading what's held doesn't wait.
 */
function useDetailBase(segment: Segment | null, fieldId: string, asking: boolean): Segment | null {
  const base = segment && { ...segment, rules: (segment.rules ?? []).filter((r) => r.fieldId !== fieldId) };
  const { settled } = useSettled(base, false);
  return asking ? settled : base;
}

export function useVoterRuleOptions(segment: Segment | null, fieldId: string, enabled: boolean) {
  const base = useDetailBase(segment, fieldId, enabled);
  return useQuery({
    queryKey: queryKeys.voterData.counts(base),
    queryFn: ({ signal }) => countsOf(api.voterData.getRuleOptions({ segment: base!, fieldId }, { signal })),
    enabled: enabled && base !== null,
    ...heldForTheSession,
    retry: voterDataRetry,
    retryDelay: voterDataRetryDelay,
  });
}

/** A vote field's voted and didn't-vote counts, measured without its own rule, asked for only while `enabled` (its election is open). */
export function useVoterVoteCounts(segment: Segment | null, fieldId: string, enabled: boolean) {
  const base = useDetailBase(segment, fieldId, enabled);
  return useQuery({
    queryKey: queryKeys.voterData.voteCounts(base, fieldId),
    queryFn: ({ signal }) => voteCountsOf(api.voterData.getVoteCounts({ segment: base!, fieldId }, { signal })),
    enabled: enabled && base !== null,
    ...heldForTheSession,
    retry: voterDataRetry,
    retryDelay: voterDataRetryDelay,
  });
}

export interface HeldCounts {
  key: SegmentKey;
  counts: VoterCounts;
  landedAt: number;
}

export interface HeldVotes {
  key: SegmentKey;
  fieldId: string;
  votes: VoteCountsResponse;
  landedAt: number;
}

export interface VoterHistory {
  counts: HeldCounts[];
  votes: HeldVotes[];
}

function readHistory(client: QueryClient): VoterHistory {
  const cache = client.getQueryCache();
  const counts = cache
    .findAll({ queryKey: queryKeys.voterData.history() })
    .filter((q) => q.state.data !== undefined)
    .map((q) => ({ key: q.queryKey[3] as SegmentKey, counts: q.state.data as VoterCounts, landedAt: q.state.dataUpdatedAt }));
  const votes = cache
    .findAll({ queryKey: queryKeys.voterData.voteHistory() })
    .filter((q) => q.state.data !== undefined)
    .map((q) => ({ key: q.queryKey[3] as SegmentKey, fieldId: q.queryKey[4] as string, votes: q.state.data as VoteCountsResponse, landedAt: q.state.dataUpdatedAt }));
  return { counts, votes };
}

export function useVoterHistory(): VoterHistory {
  const client = useQueryClient();
  const [history, setHistory] = useState(() => readHistory(client));
  useEffect(() => {
    const reread = () => setHistory(readHistory(client));
    reread();
    return client.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'success' && event.query.queryKey[0] === 'voterData') reread();
    });
  }, [client]);
  return history;
}

/**
 * Reads a plain-English request ("women over 50 who aren't Republicans") into the Segment's rules.
 * A mutation, not a query: each request is applied once, and never retried behind the customer's back.
 */
export function useInterpretSegment() {
  return useMutation({
    mutationFn: (body: InterpretSegmentRequest) => api.voterData.interpret(body),
  });
}
